import { supabase } from '../../../lib/supabase';
import { CmsData } from '../types/testPageTypes';
import { isDemoId } from '../../../utils/demoUtils';

/**
 * Service pour gérer les données de la page de test
 */
export class TestPageDataService {
  /**
   * Valide le token de route
   */
  static async validateRouteToken(token: string) {
    if (!token) return null;
    
    try {
      const { data, error } = await supabase
        .from('prospects')
        .select('*')
        .eq('token', token)
        .single();
      
      if (error) {
        console.error('Erreur lors de la validation du token:', error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error('Exception lors de la validation du token:', err);
      return null;
    }
  }

  /**
   * Charge les données CMS pour un prospect
   */
  static async loadCmsData(prospectId: string): Promise<CmsData | null> {
    try {
      // Vérifier si c'est un ID de démonstration
      if (isDemoId(prospectId)) {
        console.log('ID de démonstration détecté, retour des données CMS de démo');
        return {
          name: 'WordPress',
          version: '6.4.2',
          confidence: 92
        };
      }

      const { data: cmsResult, error } = await supabase
        .from('cms_detection_results')
        .select('*')
        .eq('prospect_id', prospectId)
        .single();
      
      if (error || !cmsResult) {
        return {
          name: 'WordPress',
          version: '6.4.2',
          confidence: 92
        };
      }
      
      return this.extractCmsInfo(cmsResult);
    } catch (error) {
      console.error('Erreur lors du chargement des données CMS:', error);
      return null;
    }
  }

  /**
   * Charge les résultats PageSpeed
   */
  static async loadPageSpeedResults(prospectId: string) {
    try {
      console.log(`🏃 Chargement PageSpeed pour prospect: ${prospectId}`);

      // Vérifier si c'est un ID de démonstration
      if (isDemoId(prospectId)) {
        console.log('ID de démonstration détecté, retour des données PageSpeed de démo');
        return {
          mobile_performance_score: 0.65,
          desktop_performance_score: 0.85
        };
      }

      const { data, error } = await supabase
        .from('pagespeed_results')
        .select('*')
        .eq('prospect_id', prospectId)
        .order('created_at', { ascending: false })
        .limit(1);

      console.log(`📊 PageSpeed query result:`, {
        error: !!error,
        errorMessage: error?.message,
        dataLength: data?.length || 0,
        hasData: !!(data && data.length > 0)
      });

      if (error || !data || data.length === 0) {
        console.log('❌ Pas de données PageSpeed trouvées, utilisation des valeurs par défaut');
        return {
          mobile_performance_score: 0.65,
          desktop_performance_score: 0.85
        };
      }

      const result = data[0];
      console.log(`✅ PageSpeed data trouvée:`, {
        mobile: result.mobile_performance_score,
        desktop: result.desktop_performance_score,
        created_at: result.created_at
      });

      return result;
    } catch (err) {
      console.error('Exception lors du chargement des données PageSpeed:', err);
      return {
        mobile_performance_score: 0.65,
        desktop_performance_score: 0.85
      };
    }
  }

  /**
   * Charge les données de mots-clés - TOUS les mots-clés
   */
  static async loadKeywordsData(prospectId: string) {
    try {
      console.log(`🔑 Chargement des mots-clés pour prospect: ${prospectId}`);

      // Vérifier si c'est un ID de démonstration
      if (isDemoId(prospectId)) {
        console.log('ID de démonstration détecté, retour des données de mots-clés de démo');
        return [
          { keyword: 'restaurant paris', position: 3, search_volume: 12000, url: 'https://example.com' },
          { keyword: 'meilleur restaurant', position: 8, search_volume: 8500, url: 'https://example.com/menu' },
          { keyword: 'cuisine française', position: 15, search_volume: 5200, url: 'https://example.com/about' },
          { keyword: 'bistrot traditionnel', position: 12, search_volume: 3400, url: 'https://example.com/bistrot' },
          { keyword: 'gastronomie parisienne', position: 7, search_volume: 2800, url: 'https://example.com/gastro' },
          { keyword: 'menu du jour', position: 18, search_volume: 4200, url: 'https://example.com/menu-jour' },
          { keyword: 'réservation restaurant', position: 5, search_volume: 6700, url: 'https://example.com/reservation' },
          { keyword: 'terrasse paris', position: 22, search_volume: 2100, url: 'https://example.com/terrasse' },
          { keyword: 'chef étoilé', position: 14, search_volume: 1900, url: 'https://example.com/chef' },
          { keyword: 'déjeuner affaires', position: 9, search_volume: 3600, url: 'https://example.com/business' },
          { keyword: 'dîner romantique', position: 11, search_volume: 2500, url: 'https://example.com/romantique' },
          { keyword: 'carte des vins', position: 16, search_volume: 1800, url: 'https://example.com/vins' },
          { keyword: 'restaurant groupe', position: 20, search_volume: 2200, url: 'https://example.com/groupe' },
          { keyword: 'cuisine du marché', position: 13, search_volume: 1600, url: 'https://example.com/marche' },
          { keyword: 'brunch paris', position: 6, search_volume: 4800, url: 'https://example.com/brunch' },
          { keyword: 'restaurant bio', position: 17, search_volume: 2900, url: 'https://example.com/bio' },
          { keyword: 'menu dégustation', position: 10, search_volume: 2300, url: 'https://example.com/degustation' },
          { keyword: 'restaurant végétarien', position: 19, search_volume: 3100, url: 'https://example.com/vegetarien' },
          { keyword: 'livraison restaurant', position: 4, search_volume: 5500, url: 'https://example.com/livraison' },
          { keyword: 'événement privé', position: 21, search_volume: 1700, url: 'https://example.com/prive' }
        ];
      }

      // Récupérer TOUS les résultats d'analyse de mots-clés pour ce prospect
      const { data, error } = await supabase
        .from('keyword_analysis_results')
        .select('*')
        .eq('prospect_id', prospectId);

      console.log(`🔑 Keywords query result:`, {
        error: !!error,
        errorMessage: error?.message,
        dataLength: data?.length || 0,
        hasData: !!(data && data.length > 0)
      });

      if (error || !data || data.length === 0) {
        console.log('❌ Aucune donnée de mots-clés trouvée pour le prospect:', prospectId);
        return [];
      }

      // Collecter tous les mots-clés de tous les résultats
      let allKeywords: any[] = [];

      data.forEach((result: any, index: number) => {
        console.log(`🔑 Processing keyword result ${index + 1}:`, {
          hasRankedKeywords: !!(result.ranked_keywords),
          rankedKeywordsLength: Array.isArray(result.ranked_keywords) ? result.ranked_keywords.length : 'not array',
          analysisType: result.analysis_type
        });

        if (result.ranked_keywords && Array.isArray(result.ranked_keywords)) {
          const keywords = result.ranked_keywords.map((kw: any) => ({
            keyword: kw.keyword,
            position: kw.rank_group || kw.position,
            search_volume: kw.search_volume || kw.volume,
            url: kw.url
          }));
          allKeywords = allKeywords.concat(keywords);
        }
      });

      console.log(`✅ Récupération de ${allKeywords.length} mots-clés pour le prospect ${prospectId}`);
      return allKeywords;
      
    } catch (err) {
      console.error('Exception lors du chargement des mots-clés:', err);
      return [];
    }
  }


  /**
   * Met à jour le statut du quiz
   */
  static async updateQuizStatus(
    prospectId: string, 
    status: 'quiz_commence' | 'quiz_en_cours' | 'quiz_fait', 
    value: boolean = true
  ) {
    try {
      // Vérifier si c'est un ID de démonstration
      if (isDemoId(prospectId)) {
        console.log(`ID de démonstration détecté, simulation de la mise à jour du statut ${status}`);
        return true;
      }

      const { error } = await supabase
        .from('prospects')
        .update({ 
          [status]: value,
          updated_at: new Date().toISOString() 
        })
        .eq('id', prospectId);
        
      if (error) {
        console.error(`Erreur lors de la mise à jour du statut ${status}:`, error);
        return false;
      }
      
      return true;
    } catch (err) {
      console.error(`Exception lors de la mise à jour du statut ${status}:`, err);
      return false;
    }
  }

  /**
   * Extrait les informations CMS à partir des données brutes
   */
  private static extractCmsInfo(data: any): CmsData | null {
    // Vérifier d'abord si on a des données directes
    if (data.cms_name) {
      return {
        name: data.cms_name,
        version: data.version || undefined,
        confidence: data.confidence || 75,
        logo_url: data.logo_url || undefined
      };
    }
    
    // Vérifier dans les détails
    if (data.details && typeof data.details === 'object') {
      const details = data.details;
      if (details.cms_name || details.name) {
        return {
          name: details.cms_name || details.name,
          version: details.version || data.version || undefined,
          confidence: details.confidence || data.confidence || 75,
          logo_url: data.logo_url || details.logo_url || undefined
        };
      }
    }
    
    // Vérifier dans les technologies
    if (data.technologies) {
      let techArray = [];
      
      if (Array.isArray(data.technologies)) {
        techArray = data.technologies;
      } else if (typeof data.technologies === 'string') {
        try {
          techArray = JSON.parse(data.technologies);
        } catch (e) {
          console.error('Erreur lors du parsing des technologies:', e);
          techArray = [];
        }
      }
      
      if (Array.isArray(techArray)) {
        // Chercher WordPress ou PrestaShop en priorité
        const cmsTech = techArray.find((tech: any) => 
          tech && (tech.name === 'WordPress' || tech.name === 'PrestaShop' || 
                 tech.name?.toLowerCase().includes('wordpress') || 
                 tech.name?.toLowerCase().includes('prestashop'))
        );
        
        if (cmsTech) {
          return {
            name: cmsTech.name,
            version: cmsTech.version || undefined,
            confidence: cmsTech.confidence || 75,
            logo_url: data.logo_url || cmsTech.logo_url || undefined
          };
        }
        
        // Si pas trouvé, prendre le premier CMS
        const anyCms = techArray.find((tech: any) => 
          tech && (tech.type === 'cms' || tech.categories?.includes('cms'))
        );
        
        if (anyCms) {
          return {
            name: anyCms.name,
            version: anyCms.version || undefined,
            confidence: anyCms.confidence || 75,
            logo_url: data.logo_url || anyCms.logo_url || undefined
          };
        }
      }
    }
    
    // Aucun CMS trouvé
    if (data && (data.cms_name === null || data.cms_name === '') && 
        (data.detected === true || data.detected === null)) {
      return {
        name: 'Site développé sur mesure',
        version: '', 
        confidence: 100, 
        description: 'Ce site semble être une solution personnalisée ou utilise des technologies non identifiables comme un CMS standard.',
        logo_url: data.logo_url || undefined
      };
    }
    
    return null;
  }
}
